/* eslint-disable */
//
// User Activity admin API (Administrator only — the server returns 403 otherwise).
// Reads the activity log recorded at sign in by PBI 4426. Goes through the shared axiosInstance
// so auth and the standard interceptors apply, same as every other admin screen.
//
import * as Api from "./api";

const BASE = "UserActivity/";
const BASE_ACCOUNT = "Account/";

// Day boundaries are worked out here, in the administrator's own timezone, and sent as UTC
// instants. Doing it on the server instead would make "today" mean whatever the server's clock
// says, which is UTC in Azure and something else on a developer machine.
export function startOfDayUtc(yyyyMmDd) {
  if (!yyyyMmDd) return "";
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

// Half open: up to, but not including, midnight after the last selected day. An inclusive end
// would silently drop everything that happened during that final day.
export function endOfDayExclusiveUtc(yyyyMmDd) {
  if (!yyyyMmDd) return "";
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  return new Date(y, m - 1, d + 1, 0, 0, 0, 0).toISOString();
}

const buildQuery = ({
  fromDate = "",
  toDate = "",
  roleId = "",
  eventType = "",
  search = "",
  sortColumn = "createdAtUtc",
  sortDirection = "desc",
} = {}) =>
  "?fromUtc=" +
  encodeURIComponent(startOfDayUtc(fromDate)) +
  "&toUtc=" +
  encodeURIComponent(endOfDayExclusiveUtc(toDate)) +
  "&roleId=" +
  encodeURIComponent(roleId || "") +
  "&eventType=" +
  encodeURIComponent(eventType || "") +
  "&search=" +
  encodeURIComponent(search || "") +
  "&sortColumn=" +
  encodeURIComponent(sortColumn || "") +
  "&sortDirection=" +
  encodeURIComponent(sortDirection || "");

// GET List?...&pageIndex=&pageSize=
export function getUserActivity(filters = {}) {
  const { pageIndex = 1, pageSize = 25 } = filters;
  const qs = buildQuery(filters) + "&pageIndex=" + pageIndex + "&pageSize=" + pageSize;
  return Api.getRequestQry(BASE + "List", qs);
}

// GET Export?... — every matching row, not just the page on screen, capped server side.
// The offset goes with it so the workbook shows the same times as the screen, and says which
// offset they are in. getTimezoneOffset is minutes BEHIND UTC, so it is negated.
export function exportUserActivity(filters = {}) {
  const offsetMinutes = -new Date().getTimezoneOffset();
  return Api.getRequestBlob(BASE + "Export" + buildQuery(filters) + "&offsetMinutes=" + offsetMinutes);
}

// Records the end of a session, so the activity log has a session end and not only a start.
//
// Called at the moment a sign out begins, while the token still works. Deliberately NOT called from
// the logout page: by then the token may have expired, and a 401 sends the shared HTTP wrapper back
// to the logout page, which would loop. A session that ends because the token simply expired
// reports nothing at all, and the presence samples are what bound it instead.
export function reportLogout(idle = false) {
  return Api.postRequest(BASE_ACCOUNT + "logout?idle=" + (idle ? "true" : "false"), {});
}

// Awaited version, for the sign out controls.
//
// Fire and forget loses a race: the click both starts this request and navigates to the logout
// route, which clears the token from storage immediately. Axios attaches the header when it
// actually dispatches, by which time the token can already be gone, and the call comes back 401
// with nothing recorded. So the report is awaited before the session is torn down, capped so a slow
// or dead API can never leave somebody unable to sign out.
export async function reportLogoutAndWait(idle = false, capMs = 3000) {
  try {
    await Promise.race([
      reportLogout(idle),
      new Promise((resolve) => setTimeout(resolve, capMs)),
    ]);
  } catch (e) {
    // Recording the end of a session must never stop somebody leaving.
  }
}

export const EXCEL_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// True only when the response really is a spreadsheet.
//
// This matters because a failed export comes back as JSON, and an API running an older build can
// come back as CSV, and the browser will save either one under the .xlsx name we asked for. Excel
// then says the file is corrupt, which sends you looking for a bug in the workbook rather than at
// what actually came over the wire.
export function isWorkbookResponse(res) {
  const type = res?.headers?.["content-type"] || res?.data?.type || "";
  return type.indexOf("spreadsheetml") !== -1;
}

// Pulls a readable message out of whatever came back instead of the file.
export async function blobMessage(data, fallback) {
  try {
    const text = data instanceof Blob ? await data.text() : String(data || "");
    if (!text) return fallback;
    if (text.trim().charAt(0) === "{") {
      const parsed = JSON.parse(text);
      return parsed?.message || parsed?.Message || fallback;
    }
    return fallback;
  } catch (e) {
    return fallback;
  }
}

// Hands the workbook to the browser as a file. A blob URL plus a download attribute keeps the
// filename; window.open would leave the browser guessing what to do with it.
export function saveWorkbookBlob(data, fileName) {
  const type = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// Today in the browser's timezone as yyyy-MM-dd, never via toISOString() which would shift the
// date across midnight for anyone east or west of UTC.
export function todayLocal() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
